import { useRef, useState, type ReactNode } from 'react';
import { analytics, useOnClickOutside } from 'libs';
import { Button, Panel } from 'components/common';

interface FilterPanelProps {
  category?: 'rates' | 'policies';
  children: ReactNode;
  footerButtonText?: string;
  headerButton: ReactNode;
  openButtonContent: ReactNode;
  openButtonTestId?: string;
  panelTestId?: string | null;
  shouldCloseOnOutsideClick?: boolean;
}

const FilterPanel = ({
  category = 'rates',
  children,
  footerButtonText = 'Show Results',
  headerButton,
  openButtonContent,
  openButtonTestId,
  panelTestId = null,
  shouldCloseOnOutsideClick = true,
}: FilterPanelProps) => {
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleClickOutside = () => {
    if (!shouldCloseOnOutsideClick || !isOpen) return;
    onClose();
  };

  useOnClickOutside(handleClickOutside, [buttonRef, panelRef]);

  const onClose = () => {
    analytics.productSearch.filterPanelClose(category);
    setIsOpen(false);
  };

  const onFooterButtonClick = () => {
    analytics.productSearch.filterPanelShowResultsClick(category);
    setIsOpen(false);
  };

  const onOpen = () => {
    analytics.productSearch.filterPanelOpen(category);
    setIsOpen(true);
  };

  const toggleOpen = () => (isOpen ? onClose() : onOpen());

  return (
    <>
      <div className="panel__button bg-athens-gray position-sticky mb-3 pt-2 z-1">
        <Button onClick={toggleOpen} ref={buttonRef} testId={openButtonTestId} variant="primary-dark" width="block">
          {openButtonContent}
        </Button>
      </div>
      <Panel isOpen={isOpen} ref={panelRef} testId={panelTestId as string | undefined}>
        <Panel.Header onClose={onClose}>
          <div className="w-2/5 text-nowrap">{headerButton}</div>
          <div className="w-1/4 text-1_1875 text-center">Filters</div>
        </Panel.Header>
        <Panel.Body>{children}</Panel.Body>
        <Panel.FooterButton onClick={onFooterButtonClick}>{footerButtonText}</Panel.FooterButton>
      </Panel>
    </>
  );
};

export default FilterPanel;
